// Wallpaper collection constants
export const WALLPAPERS = [
  {
    id: 'default',
    name: 'No Wallpaper',
    path: null,
    thumbnail: null,
    description: 'Default chat background'
  }
];

// Helper function to load all wallpapers from the public/wallpapers directory
export const loadWallpapers = async () => {
  try {
    const baseWallpapers = [
      {
        id: 'default',
        name: 'No Wallpaper',
        path: null,
        thumbnail: null,
        description: 'Default chat background'
      }
    ];
    
    // Get list of wallpapers from public/wallpapers
    const response = await fetch('/wallpapers/index.json');
    
    if (!response.ok) {
      console.warn('Could not load wallpaper index. Using only default wallpaper.');
      return baseWallpapers;
    }
    
    const wallpapersList = await response.json();
    
    // Map the wallpaper list to the format we need
    const mappedWallpapers = wallpapersList.map(file => {
      // Skip thumbnails or use them as thumbnails for the actual images
      if (file.toLowerCase().includes('-150x150')) {
        return null;
      }
      
      // Create a thumbnail path based on the file name
      let thumbnailPath = null;
      if (file.includes('.')) {
        const fileName = file.substring(0, file.lastIndexOf('.'));
        const extension = file.substring(file.lastIndexOf('.'));
        
        // Check if thumbnail exists with -150x150 suffix
        if (wallpapersList.includes(`${fileName}-150x150${extension}`)) {
          thumbnailPath = `/wallpapers/${fileName}-150x150${extension}`;
        }
      }
      
      // Extract file extension to store with the ID
      const fileExtension = file.substring(file.lastIndexOf('.'));
      
      return {
        id: file, // Keep the full filename as ID to preserve extension information
        name: file.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '), // Format name
        path: `/wallpapers/${file}`,
        extension: fileExtension, // Store the extension
        thumbnail: thumbnailPath || `/wallpapers/${file}`,
        description: `Wallpaper ${file}`
      };
    }).filter(Boolean); // Remove null entries
    
    return [...baseWallpapers, ...mappedWallpapers];
  } catch (error) {
    console.error('Error loading wallpapers:', error);
    return [
      {
        id: 'default',
        name: 'No Wallpaper',
        path: null,
        thumbnail: null,
        description: 'Default chat background'
      }
    ];
  }
};

// Helper functions for wallpapers
export const getWallpaperById = (wallpapers, id) => {
  return wallpapers.find(wp => wp.id === id) || wallpapers[0];
};

export const getWallpaperStyle = (wallpaper, customWallpaper, scaling = 'cover') => {
  if (!wallpaper || wallpaper === 'default') {
    return {};
  }

  // For custom uploaded wallpaper
  if (wallpaper === 'custom' && customWallpaper) {
    return getScalingStyle(customWallpaper, scaling);
  }

  // For image from wallpapers directory - use the full path
  // wallpaper is now stored as the full filename
  return getScalingStyle(`/wallpapers/${wallpaper}`, scaling);
};

// Function to determine the appropriate background-size based on scaling option
export const getScalingStyle = (imagePath, scaling = 'cover') => {
  const baseStyle = {
    backgroundImage: `url(${imagePath})`,
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  };

  switch (scaling) {
    case 'contain':
      return {
        ...baseStyle,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
      };
    case 'actual':
      return {
        ...baseStyle,
        backgroundSize: 'auto',
        backgroundPosition: 'center',
      };
    case 'tile':
      return {
        ...baseStyle,
        backgroundSize: 'auto',
        backgroundRepeat: 'repeat',
      };
    case 'center':
      return {
        ...baseStyle,
        backgroundSize: 'auto',
        backgroundPosition: 'center',
      };
    case 'cover':
    default:
      return {
        ...baseStyle,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
  }
};

// Available scaling options for wallpapers
export const SCALING_OPTIONS = [
  { value: 'cover', label: 'Fill (Cover)' },
  { value: 'contain', label: 'Fit (Contain)' },
  { value: 'actual', label: 'Actual Size' },
  { value: 'tile', label: 'Tile' },
  { value: 'center', label: 'Center' }
];